/**
 * Recording — capture the player's performance for sharing.
 *
 * Three modes, all share one Recorder instance:
 *   • 'audio' — just the đàn-tranh mix (tap off the engine's limiter).
 *                Output: .webm / .m4a.
 *   • 'video' — silent stage composite: live <video> + every overlay
 *                canvas inside the stage (hand skeletons + strings +
 *                flashes), drawn into an offscreen canvas each frame.
 *                Output: .webm / .mp4.
 *   • 'full'  — composite + the audio mix, ready to share.
 *
 * The composite is driven from a rAF loop that the Recorder owns — it
 * stops on stop(). A subtle gold "Air Đàn Tranh" watermark is burned
 * into the bottom-right.
 */

const MIME_VIDEO = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=h264,opus',
  'video/webm',
];
const MIME_AUDIO = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
];

function pickMime(list: string[]): string | null {
  if (!('MediaRecorder' in window)) return null;
  for (const m of list) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* type not supported */
    }
  }
  return null;
}

function extFor(mime: string | null): string {
  if (!mime) return 'webm';
  if (mime.startsWith('audio/mp4')) return 'm4a';
  if (mime.startsWith('video/mp4')) return 'mp4';
  return 'webm';
}

export type RecordMode = 'audio' | 'video' | 'full';
export type RecorderState = 'idle' | 'recording';
export type OnRecorderStateChange = (
  state: RecorderState,
  mode: RecordMode | null,
  blob?: Blob,
) => void;

export interface RecorderDeps {
  /** AudioContext to tap. */
  ctx: AudioContext;
  /** The pre-limiter bus to fan out for the audio track. */
  audioBus: AudioNode;
  /** Function returning the live <video> + the list of overlay canvases
   *  to composite, in draw order. Resolved per-frame so a React remount
   *  of the stage doesn't strand the recorder on stale elements. */
  getSources: () => {
    video: HTMLVideoElement | null;
    overlays: HTMLCanvasElement[];
    /** Whether the camera feed is mirrored on screen — the recorded
     *  composite mirrors to match. */
    mirrored: boolean;
    /** Size of the stage div, in CSS px. The composite samples down
     *  to a 720p-short-side resolution preserving this aspect. */
    stageWidth: number;
    stageHeight: number;
  };
  onStateChange?: OnRecorderStateChange;
}

export class Recorder {
  state: RecorderState = 'idle';
  mode: RecordMode | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private videoTrack: MediaStreamTrack | null = null;
  private manualFrames = false;
  private rafId: number | null = null;
  private lastBlob: Blob | null = null;

  private composeCanvas: HTMLCanvasElement;
  private composeCtx: CanvasRenderingContext2D;
  private composeW = 0;
  private composeH = 0;

  private audioDest: MediaStreamAudioDestinationNode;
  private readonly deps: RecorderDeps;

  constructor(deps: RecorderDeps) {
    this.deps = deps;
    this.composeCanvas = document.createElement('canvas');
    const c = this.composeCanvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
    });
    if (!c) throw new Error('Recorder: 2D context unavailable');
    this.composeCtx = c;
    this.composeCtx.imageSmoothingEnabled = true;
    this.composeCtx.imageSmoothingQuality = 'high';

    this.audioDest = deps.ctx.createMediaStreamDestination();
    deps.audioBus.connect(this.audioDest);
  }

  isSupported(): boolean {
    return 'MediaRecorder' in window;
  }

  start(mode: RecordMode): void {
    if (this.state === 'recording') return;
    if (!this.isSupported()) {
      console.warn("Recorder: browser doesn't support MediaRecorder.");
      return;
    }
    this.mode = mode;
    this.chunks = [];
    this.lastBlob = null;

    let stream: MediaStream;
    let mime: string | null;
    if (mode === 'audio') {
      stream = this.audioDest.stream;
      mime = pickMime(MIME_AUDIO);
    } else {
      this.sizeCompose();
      let cStream: MediaStream;
      try {
        cStream = this.composeCanvas.captureStream(0);
      } catch {
        cStream = this.composeCanvas.captureStream(60);
      }
      this.videoTrack = cStream.getVideoTracks()[0]!;
      this.manualFrames =
        typeof (this.videoTrack as MediaStreamTrack & { requestFrame?: () => void })
          .requestFrame === 'function';
      if (!this.manualFrames) {
        try {
          this.videoTrack.stop();
        } catch {
          /* already stopped */
        }
        cStream = this.composeCanvas.captureStream(60);
        this.videoTrack = cStream.getVideoTracks()[0]!;
      }
      const tracks: MediaStreamTrack[] = [this.videoTrack];
      if (mode === 'full') {
        const aTrack = this.audioDest.stream.getAudioTracks()[0];
        if (aTrack) tracks.push(aTrack);
      }
      stream = new MediaStream(tracks);
      mime = pickMime(MIME_VIDEO);
      this.composeFrame();
    }
    if (!mime) {
      console.warn('Recorder: no compatible codec found.');
      try {
        this.videoTrack?.stop();
      } catch {
        /* swallow */
      }
      this.videoTrack = null;
      return;
    }
    try {
      const videoBits = mode === 'audio' ? undefined : 12_000_000;
      const audioBits = mode === 'video' ? undefined : 192_000;
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: mime,
        ...(videoBits !== undefined ? { videoBitsPerSecond: videoBits } : {}),
        ...(audioBits !== undefined ? { audioBitsPerSecond: audioBits } : {}),
      });
    } catch (e) {
      console.error('Recorder failed to start:', e);
      try {
        this.videoTrack?.stop();
      } catch {
        /* swallow */
      }
      this.videoTrack = null;
      return;
    }
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => {
      try {
        this.videoTrack?.stop();
      } catch {
        /* swallow */
      }
      this.videoTrack = null;
      this.manualFrames = false;
      const blob = new Blob(this.chunks, { type: mime! });
      this.lastBlob = blob;
      this.download(blob, mime);
      this.state = 'idle';
      const savedMode = this.mode;
      this.deps.onStateChange?.(this.state, savedMode, blob);
    };
    this.mediaRecorder.start(250);
    this.state = 'recording';
    this.startedAt = performance.now();
    this.deps.onStateChange?.(this.state, this.mode);

    // Drive composite frames from rAF for video/full modes. The recorder
    // owns its own loop so it doesn't depend on the gesture loop being
    // active — gesture detection is paused while the tuner is open, but
    // recording should still keep ticking.
    if (this.mode !== 'audio') {
      const loop = () => {
        if (this.state !== 'recording') return;
        this.composeFrame();
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    }
  }

  stop(): void {
    if (this.state !== 'recording' || !this.mediaRecorder) return;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    try {
      this.mediaRecorder.stop();
    } catch {
      /* may already be inactive */
    }
  }

  elapsedMs(): number {
    return this.state === 'recording' ? performance.now() - this.startedAt : 0;
  }

  getLastBlob(): Blob | null {
    return this.lastBlob;
  }

  dispose(): void {
    this.stop();
    try {
      this.audioDest.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  private sizeCompose(): void {
    const { stageWidth, stageHeight } = this.deps.getSources();
    const W = Math.max(2, Math.round(stageWidth));
    const H = Math.max(2, Math.round(stageHeight));
    const CAP = 720;
    const short = Math.min(W, H);
    const scale = Math.min(1, CAP / short);
    let outW = Math.round(W * scale);
    let outH = Math.round(H * scale);
    outW = outW & ~1;
    outH = outH & ~1;
    this.composeCanvas.width = outW;
    this.composeCanvas.height = outH;
    this.composeCtx.imageSmoothingEnabled = true;
    this.composeCtx.imageSmoothingQuality = 'high';
    this.composeW = outW;
    this.composeH = outH;
  }

  private composeFrame(): void {
    if (this.state !== 'recording') return;
    if (this.mode === 'audio') return;
    const ctx = this.composeCtx;
    const W = this.composeW;
    const H = this.composeH;
    if (!W || !H) return;
    const { video, overlays, mirrored } = this.deps.getSources();

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);

    // Camera frame — match the on-screen mirror state so the share
    // looks like the live view. object-fit: fill on the video element
    // means we should also stretch to (W, H), not "cover".
    if (video && video.videoWidth && video.videoHeight) {
      ctx.save();
      if (mirrored) {
        ctx.translate(W, 0);
        ctx.scale(-1, 1);
      }
      try {
        ctx.drawImage(video, 0, 0, W, H);
      } catch {
        /* video not yet decodable */
      }
      ctx.restore();
    }

    // Overlay canvases — drawn in document order. HandOverlay sits
    // inside a CSS-mirrored container when mirrorCamera is on, so we
    // mirror its blit here too. Other overlays render in normal-space.
    for (let i = 0; i < overlays.length; i++) {
      const ov = overlays[i]!;
      if (!ov.width || !ov.height) continue;
      const mirroredOverlay = mirrored && ov.dataset.mirrored === 'true';
      ctx.save();
      if (mirroredOverlay) {
        ctx.translate(W, 0);
        ctx.scale(-1, 1);
      }
      try {
        ctx.drawImage(ov, 0, 0, W, H);
      } catch {
        /* swallow */
      }
      ctx.restore();
    }

    this.drawWatermark(ctx, W, H);

    if (this.manualFrames && this.videoTrack) {
      const t = this.videoTrack as MediaStreamTrack & { requestFrame?: () => void };
      try {
        t.requestFrame?.();
      } catch {
        /* swallow */
      }
    }
  }

  private drawWatermark(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const pad = Math.max(10, Math.round(H * 0.018));
    const fs = Math.max(12, Math.round(H * 0.022));
    ctx.save();
    ctx.font = `600 ${fs}px -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.shadowColor = 'rgba(0,0,0,0.85)';
    ctx.shadowBlur = 6;
    ctx.fillStyle = 'rgba(255, 210, 74, 0.92)';
    ctx.fillText('Air Đàn Tranh', W - pad, H - pad);
    ctx.restore();
  }

  private download(blob: Blob, mime: string | null): void {
    const ext = extFor(mime);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `air-dantranh-${this.mode}-${stamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}
